const FONT_FAMILY = 'IBM Plex Mono';
const FONT_SOURCE = 'url(https://fonts.gstatic.com/s/ibmplexmono/v19/-F63fjptAgt5VM-kVkqdyU8n1isq131nj-otFQ.woff2)';

/**
 * Shared across every mount, so only the first one pays for the download and
 * every later drawer opens with the font already in hand.
 */
let loading: Promise<void> | null = null;

/**
 * The receipt's monospace face.
 *
 * A receipt is a monospace grid - lines are padded to a fixed width and centred
 * by that padding - so drawing it in whatever serif the browser falls back to
 * does not merely look wrong, it lines up wrong. The canvas therefore waits for
 * this rather than drawing immediately and redrawing later: the first paint was
 * landing before the font arrived, and a receipt in Times New Roman is what
 * people saw.
 */
class ReceiptFont {
    static readonly FAMILY = FONT_FAMILY;

    /**
     * What to assign to `ctx.font`, fallback included.
     *
     * Without one, a browser that cannot resolve the family at that instant falls
     * back to its *default* face - a serif - and a receipt rendered in Times has
     * neither the look nor the fixed advance width the layout depends on. Naming
     * monospace means the worst case is still a grid.
     */
    static readonly STACK = `"${FONT_FAMILY}", monospace`;

    /** Resolves once the face is usable by a canvas - or once it is clear it never will be. */
    static ready(): Promise<void> {
        loading ??= ReceiptFont.fetch();
        return loading;
    }

    private static async fetch(): Promise<void> {
        try {
            const face = new FontFace(FONT_FAMILY, FONT_SOURCE);
            await face.load();
            document.fonts.add(face);
            // Registering the face and having it ready to draw with are separate
            // steps; without this the first fillText can still use the fallback.
            await document.fonts.load(`16px "${FONT_FAMILY}"`);
            // Resolves once the document has finished loading and applying fonts.
            // face.load() alone says the bytes arrived, not that the face is ready
            // to be selected by a canvas.
            await document.fonts.ready;
        } catch {
            // Offline, blocked, or a CSP that dislikes the CDN. Resolve anyway -
            // a receipt in the fallback face beats no receipt at all.
        }
    }
}

export { ReceiptFont };
